/*
 * scroll-lock.js — モーダル/ドロワー展開中の背面タッチスクロール遮断（JS バックストップ）
 *
 * 第一防御は component-scroll-lock.css の touch-action（宣言的・ネイティブ）。本スクリプトは
 * iOS WebKit の取りこぼし経路への保険として、ロック中の touchmove を監視し、モーダル/ドロワー
 * 内のスクロール可能領域が実際に消費できるパン以外を preventDefault で抑止する。
 *
 * リスナーのライフサイクル（崩すと壊れる制約）:
 * - 非 passive の touchmove リスナーは「ロック中」（body にロッククラスがある間）だけ
 *   MutationObserver で登録する。常設すると通常時の全スクロール開始が main thread 待ちに
 *   なり全ページへ性能回帰を持ち込む。逆に「ジェスチャ中のみ登録」（touchstart 内で後付け）
 *   にしてはならない: WebKit はジェスチャ開始時点で cancelable 判定を固定するため、後付け
 *   リスナーには touchmove が non-cancelable で届き preventDefault が無効になる（iOS 26 実測・
 *   2026-07-13）。
 *
 * 遮断の判定（body-scroll-lock 方式・方向判定つき）:
 * - iOS は overscroll-behavior: contain があっても「端に達した内側スクロールから開始した
 *   パン」をページへハンドオフする既知の穴がある。そのため許可は「スクローラーが存在する」
 *   でなく「スクローラーがそのドラッグ方向を実際に消費できる」まで絞り、端方向のパンは
 *   preventDefault で止める。
 * - ロック中はピンチ（多指）も凍結する: ピンチでビジュアルビューポートがズームすると、
 *   そのままのパンで fixed のドロワーと背面ページが相対移動し「背面が動く」体験になるため
 *   （2026-07-13 オーナー実機レビュー）。OS レベルのアクセシビリティズームには影響しない。
 * - フォームコントロール起点（input/textarea/select）は常に通す（ファセットのレンジ
 *   スライダー等のネイティブドラッグを壊さないため）。
 * 設計の正本は docs/touch_scroll_lock.md。
 */

(() => {
  const LOCK_CLASSES = [
    'overflow-hidden',
    'overflow-hidden-mobile',
    'overflow-hidden-tablet',
    'overflow-hidden-desktop',
  ];

  /* ロック中に「内部スクロールを許可する」モーダル/ドロワー系カスタム要素。
     新しいモーダル/ドロワーを追加したらここに要素名を登録する。 */
  const OVERLAY_COMPONENTS = [
    'menu-drawer',
    'header-drawer',
    'modal-dialog',
    'quick-add-modal',
    'product-modal',
    'details-modal',
    'password-modal',
    'cart-drawer',
    'pickup-availability-drawer',
    'localization-form',
  ].join(',');

  /* 実効ロック判定: base.css のブレークポイント条件込みの実効値（computed overflow）で
     確定する。リスナー登録はクラス存在ベース（下記 sync）なので、ここが二重の門になる。 */
  const isBodyLocked = () => window.getComputedStyle(document.body).overflow === 'hidden';

  const findAllowedScroller = (target) => {
    if (!(target instanceof Element)) return null;
    if (!target.closest(OVERLAY_COMPONENTS)) return null;
    for (let node = target; node && node !== document.body; node = node.parentElement) {
      const style = window.getComputedStyle(node);
      const scrollableY = /auto|scroll/.test(style.overflowY) && node.scrollHeight > node.clientHeight;
      const scrollableX = /auto|scroll/.test(style.overflowX) && node.scrollWidth > node.clientWidth;
      if (scrollableY || scrollableX) return node;
    }
    return null;
  };

  /* スクローラーがこのドラッグ方向を消費できるか（支配軸のみ・端では false）。
     dy > 0 は指が下方向＝コンテンツは上端方向へ動く＝scrollTop に残りが必要。 */
  const scrollerCanConsume = (scroller, dx, dy) => {
    if (Math.abs(dy) >= Math.abs(dx)) {
      if (dy > 0) return scroller.scrollTop > 0;
      if (dy < 0) return scroller.scrollTop < scroller.scrollHeight - scroller.clientHeight - 1;
      return true;
    }
    if (dx > 0) return scroller.scrollLeft > 0;
    if (dx < 0) return scroller.scrollLeft < scroller.scrollWidth - scroller.clientWidth - 1;
    return true;
  };

  let gesture = null;

  const beginGesture = (touch) => {
    const target = touch.target;
    gesture = {
      locked: isBodyLocked(),
      formControl: target instanceof Element && !!target.closest('input, textarea, select'),
      scroller: findAllowedScroller(target),
      lastX: touch.clientX,
      lastY: touch.clientY,
    };
  };

  const onTouchStart = (event) => {
    beginGesture(event.touches[0]);
  };

  const onTouchMove = (event) => {
    if (!gesture || !gesture.locked || gesture.formControl) return;
    if (!event.cancelable) return;
    if (event.touches.length > 1) {
      event.preventDefault();
      return;
    }
    const touch = event.touches[0];
    const dx = touch.clientX - gesture.lastX;
    const dy = touch.clientY - gesture.lastY;
    gesture.lastX = touch.clientX;
    gesture.lastY = touch.clientY;
    if (!gesture.scroller || !scrollerCanConsume(gesture.scroller, dx, dy)) event.preventDefault();
  };

  /* 指が減った時: 全て離れたらジェスチャ終了、残っていれば残存タッチの開始要素
     （Touch.target はタッチ開始要素に固定される）から判定を作り直す。 */
  const onTouchSettled = (event) => {
    if (event.touches.length === 0) {
      gesture = null;
      return;
    }
    beginGesture(event.touches[0]);
  };

  let attached = false;

  const sync = () => {
    const shouldAttach = LOCK_CLASSES.some((name) => document.body.classList.contains(name));
    if (shouldAttach === attached) return;
    attached = shouldAttach;
    if (shouldAttach) {
      document.addEventListener('touchstart', onTouchStart, { passive: true });
      document.addEventListener('touchmove', onTouchMove, { passive: false });
      document.addEventListener('touchend', onTouchSettled, { passive: true });
      document.addEventListener('touchcancel', onTouchSettled, { passive: true });
    } else {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchSettled);
      document.removeEventListener('touchcancel', onTouchSettled);
      gesture = null;
    }
  };

  new MutationObserver(sync).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  sync();
})();
